import React, { useState, useRef, useEffect } from 'react';
import { useAuth } from '../hooks/useAuth';
import { PageHeader } from '../components/PageHeader';
import { Row, Col } from '../components/Layout';
import { userAPI, postsAPI, friendsAPI, getImageUrl } from '../services/api';
import { SafeImage } from '../components/SafeImage';
import imageCompression from 'browser-image-compression';

interface ProfilePageProps {
  profileUserId?: string | null;
  onNavigate?: (page: string) => void;
  onViewProfile?: (userId: string) => void;
  onStartVoiceRoom?: (data: { title: string; category: string; type: 'voice' | 'video' }) => void;
  onMessageUser?: (userId: string) => void;
}

const mockFollowList = [
  { _id: '101', username: 'AhmetY', profileImage: 'https://i.pravatar.cc/100?img=11', isFollowing: true },
  { _id: '102', username: 'ZeynepK', profileImage: 'https://i.pravatar.cc/100?img=5', isFollowing: false },
  { _id: '103', username: 'MehmetG', profileImage: 'https://i.pravatar.cc/100?img=15', isFollowing: true },
  { _id: '104', username: 'Elif_34', profileImage: 'https://i.pravatar.cc/100?img=20', isFollowing: false },
  { _id: '105', username: 'gaming_pro', profileImage: 'https://i.pravatar.cc/100?img=33', isFollowing: true },
];

const ProfilePage: React.FC<ProfilePageProps> = ({ profileUserId, onNavigate, onViewProfile, onMessageUser }) => {
  const [tab, setTab] = useState('posts');
  const [hover, setHover] = useState(false);
  const { user, updateUser } = useAuth();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isUploading, setIsUploading] = useState(false);

  // Profile Viewer State
  const [profileData, setProfileData] = useState<any | null>(null);
  const isOwnProfile = !profileUserId || profileUserId === (user?.id || (user as any)?._id);

  const [posts, setPosts] = useState<any[]>([]);
  const [isPosting, setIsPosting] = useState(false);
  const postInputRef = useRef<HTMLInputElement>(null);
  
  // Post Interaction State
  const [selectedPost, setSelectedPost] = useState<any | null>(null);
  const [commentText, setCommentText] = useState('');
  
  // Profile edit & share state
  const [isEditingProfile, setIsEditingProfile] = useState(false);
  const [editBio, setEditBio] = useState(user?.bio || "Buddy Live'da yeni bir yayıncıyım. Takip etmeyi unutmayın! 🎮✨");
  const [editUsername, setEditUsername] = useState(user?.username || '');
  const [isCopied, setIsCopied] = useState(false);
  
  // Follow List Modal State
  const [followModalType, setFollowModalType] = useState<'followers' | 'following' | null>(null);

  useEffect(() => {
    const fetchId = isOwnProfile ? (user?.id || (user as any)?._id) : profileUserId;
    
    if (fetchId) {
      if (!isOwnProfile) {
        userAPI.getUserProfile(fetchId)
           .then(data => setProfileData(data))
           .catch(err => console.error('Profil yüklenemedi:', err));
      } else {
        setProfileData(user); // fallback to own user
      }

      postsAPI.getPosts(fetchId)
        .then(data => {
          if (Array.isArray(data)) setPosts(data);
          else {
            console.error('getPosts returned non-array:', data);
            setPosts([]);
          }
        })
        .catch(err => {
          console.error('Gönderiler yüklenemedi:', err);
          setPosts([]);
        });
    }
  }, [user, profileUserId, isOwnProfile]);

  const handlePostUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      if (file.size > 20 * 1024 * 1024) {
        alert('Dosya boyutu çok büyük! Lütfen 20MB altı bir dosya seçin.');
        return;
      }
      setIsPosting(true);
      try {
        const options = { maxSizeMB: 1, maxWidthOrHeight: 1920, useWebWorker: true };
        const compressedFile = await imageCompression(file, options);
        
        const res = await postsAPI.createPost(compressedFile, '');
        setPosts(prev => [res.post, ...prev]);
      } catch (error) {
        console.error('Yükleme hatası:', error);
        alert('Gönderi yüklenemedi. Lütfen tekrar deneyin.');
      } finally {
        setIsPosting(false);
        if (postInputRef.current) postInputRef.current.value = '';
      }
    }
  };

  const handleLikePost = async (postId: string) => {
    try {
      const res = await postsAPI.likePost(postId);
      setPosts(prev => prev.map(p => p._id === postId ? res.post : p));
      if (selectedPost && selectedPost._id === postId) {
        setSelectedPost(res.post);
      }
    } catch (error) {
      console.error('Beğeni hatası:', error);
    }
  };

  const handleAddComment = async (postId: string) => {
    if (!commentText.trim()) return;
    try {
      const res = await postsAPI.addComment(postId, commentText);
      setPosts(prev => prev.map(p => p._id === postId ? res.post : p));
      if (selectedPost && selectedPost._id === postId) {
        setSelectedPost(res.post);
      }
      setCommentText('');
    } catch (error) {
      console.error('Yorum ekleme hatası:', error);
    }
  };

  const handleFollowUser = async () => {
    if (!profileData || !profileData._id) return;
    try {
      await friendsAPI.sendRequest(profileData._id);
      alert('Takip isteği gönderildi!');
    } catch (error) {
      console.error('Takip etme hatası:', error);
      alert('Zaten takip ediyorsunuz veya bir hata oluştu.');
    }
  };

  const displayUser = isOwnProfile ? user : profileData;

  const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      if (file.size > 20 * 1024 * 1024) {
        alert('Dosya boyutu çok büyük! Lütfen 20MB altı bir dosya seçin.');
        return;
      }
      setIsUploading(true);
      try {
        const options = { maxSizeMB: 0.5, maxWidthOrHeight: 1080, useWebWorker: true };
        const compressedFile = await imageCompression(file, options);

        const res = await userAPI.uploadProfileImage(compressedFile);
        updateUser({ profileImage: res.profileImage });
      } catch (error) {
        console.error('Yükleme hatası:', error);
        alert('Profil fotoğrafı yüklenemedi. Lütfen tekrar deneyin.');
      } finally {
        setIsUploading(false);
        if(fileInputRef.current) fileInputRef.current.value = '';
      }
    }
  };

  const handleShare = () => {
    navigator.clipboard.writeText(window.location.href);
    setIsCopied(true);
    setTimeout(() => setIsCopied(false), 2000);
  };

  const handleSaveProfile = async () => {
    try {
      const res = await userAPI.updateProfile({ username: editUsername, bio: editBio });
      updateUser({ username: editUsername, bio: editBio });
      if (isOwnProfile) {
        setProfileData({ ...profileData, username: editUsername, bio: editBio });
      }
      setIsEditingProfile(false);
    } catch (error) {
      console.error('Profil güncelleme hatası:', error);
      alert('Profil güncellenirken bir hata oluştu. Kullanıcı adı alınmış olabilir.');
    }
  };

  const handleDeletePost = async (postId: string) => {
    if (!window.confirm('Bu gönderiyi silmek istediğinize emin misiniz?')) return;
    try {
      await postsAPI.deletePost(postId);
      setPosts(prev => prev.filter(p => p._id !== postId));
      if (selectedPost && selectedPost._id === postId) {
        setSelectedPost(null);
      }
    } catch (error) {
      console.error('Gönderi silme hatası:', error);
      alert('Gönderi silinemedi.');
    }
  };

  return (
    <div style={{ height: '100%', position: 'relative', background: 'var(--bg)', animation: 'slideL .4s ease', overflowY: 'auto', paddingBottom: 85 }}>
      {/* Cover Image */}
      <div style={{ position: 'relative', height: 180, overflow: 'hidden' }}>
        <img src="https://images.unsplash.com/photo-1550745165-9bc0b252726f?w=800" alt="Cover" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
        <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(to top,var(--bg) 0%,rgba(10,10,15,0) 100%)' }} />
        <div style={{ position: 'absolute', top: 0, left: 0, right: 0, zIndex: 10 }}>
          <PageHeader
            title=""
            right={
              <>
                <button className="icon-btn" style={{ background: 'rgba(0,0,0,.3)', border: 'none', color: '#fff' }}>🔗</button>
                <button onClick={() => onNavigate && onNavigate('account')} className="icon-btn" style={{ background: 'rgba(0,0,0,.3)', border: 'none', color: '#fff' }}>⚙️</button>
              </>
            }
          />
        </div>
      </div>

      <div style={{ padding: '0 18px', marginTop: -50, position: 'relative', zIndex: 5 }}>
        {/* Avatar & Basic Info */}
        <Row style={{ gap: 16, alignItems: 'flex-end', marginBottom: 20 }}>
          <div style={{ position: 'relative' }}>
            <div 
              onClick={() => isOwnProfile && fileInputRef.current?.click()}
              onMouseEnter={() => isOwnProfile && setHover(true)}
              onMouseLeave={() => isOwnProfile && setHover(false)}
              style={{ width: 90, height: 90, borderRadius: 28, padding: 3, background: 'linear-gradient(135deg,var(--coral),var(--purple))', cursor: isOwnProfile ? 'pointer' : 'default', position: 'relative' }}>
              <div style={{ width: '100%', height: '100%', borderRadius: 25, border: '4px solid var(--bg)', overflow: 'hidden', background: 'var(--surface)', position: 'relative' }}>
                <SafeImage 
                  src={displayUser?.profileImage ? getImageUrl(displayUser.profileImage) : undefined} 
                  fallbackSrc={`https://ui-avatars.com/api/?name=${displayUser?.username}&background=random`}
                  alt={displayUser?.username} 
                  style={{ width: '100%', height: '100%', objectFit: 'cover', opacity: isUploading ? 0.5 : 1 }} 
                />
                
                {/* Overlay with camera icon */}
                {isOwnProfile && <div style={{
                  position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.4)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  opacity: isUploading || hover ? 1 : 0, transition: 'opacity 0.2s',
                  pointerEvents: 'none' // Let clicks pass to parent
                }}>
                  {isUploading ? (
                    <div style={{
                       width: 24, height: 24, borderRadius: '50%',
                       border: '3px solid rgba(255,255,255,0.3)', borderTopColor: '#fff',
                       animation: 'spin 1s linear infinite'
                    }} />
                  ) : (
                    <span style={{ fontSize: 24 }}>📷</span>
                  )}
                </div>}
              </div>
              
              <input type="file" ref={fileInputRef} hidden accept="image/*" onChange={(e) => { e.stopPropagation(); handleImageUpload(e); }} onClick={(e) => e.stopPropagation()} />
            </div>
            {displayUser?.isLive && <div style={{ position: 'absolute', bottom: 4, right: 4, width: 18, height: 18, background: 'var(--green)', borderRadius: '50%', border: '4px solid var(--bg)', pointerEvents: 'none' }} />}
          </div>
          <Col style={{ paddingBottom: 6 }}>
            <Row style={{ alignItems: 'center', gap: 6 }}>
              <div className="syne" style={{ fontSize: 22, fontWeight: 800 }}>{displayUser?.username}</div>
              <div style={{ background: 'var(--blue)', color: '#fff', fontSize: 10, width: 16, height: 16, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>✓</div>
            </Row>
            <div style={{ fontSize: 13, color: 'var(--muted)', marginTop: 2 }}>@{displayUser?.username?.toLowerCase()}</div>
          </Col>
        </Row>

        {/* Bio */}
        {isEditingProfile ? (
          <div style={{ marginBottom: 20 }}>
            <input 
              value={editUsername}
              onChange={(e) => setEditUsername(e.target.value)}
              style={{
                width: '100%', background: 'var(--surface)', color: 'var(--text)',
                border: '1px solid var(--border)', borderRadius: 12, padding: 12,
                fontSize: 14, fontFamily: 'inherit', marginBottom: 12
              }}
              placeholder="Kullanıcı Adı"
            />
            <textarea 
              value={editBio}
              onChange={(e) => setEditBio(e.target.value)}
              style={{
                width: '100%', minHeight: 60, background: 'var(--surface)', color: 'var(--text)',
                border: '1px solid var(--border)', borderRadius: 12, padding: 12,
                fontSize: 14, fontFamily: 'inherit', resize: 'vertical'
              }}
              placeholder="Kendinden bahset..."
            />
          </div>
        ) : (
          <div style={{ fontSize: 14, lineHeight: 1.5, color: 'var(--text)', marginBottom: 20 }}>
            {displayUser?.bio || "Buddy Live'da yeni bir yayıncıyım. Takip etmeyi unutmayın! 🎮✨"}
          </div>
        )}

        {/* Badges */}
        <Row style={{ gap: 8, marginBottom: 24, overflowX: 'auto' }} className="hide-scrollbar">
          {[
            { icon: '💎', label: 'Top Gifters', color: 'var(--cyan)' },
            { icon: '🔥', label: '100+ Streamer', color: 'var(--coral)' },
            { icon: '⭐', label: 'Pro', color: 'var(--gold)' },
          ].map((b, i) => (
            <div key={i} style={{
              background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 10,
              padding: '6px 12px', display: 'flex', alignItems: 'center', gap: 6, flexShrink: 0
            }}>
              <span style={{ fontSize: 14 }}>{b.icon}</span>
              <span style={{ fontSize: 11, fontWeight: 600, color: b.color }}>{b.label}</span>
            </div>
          ))}
        </Row>

        {/* Stats */}
        <Row style={{ background: 'var(--surface)', padding: '16px', borderRadius: 20, border: '1px solid var(--border)', justifyContent: 'space-around', marginBottom: 24 }}>
          {[
            { id: 'followers', label: 'Takipçi', val: displayUser?.followers ?? 0, action: () => setFollowModalType('followers') },
            { id: 'following', label: 'Takip', val: displayUser?.following ?? 0, action: () => setFollowModalType('following') },
            { id: 'streams', label: 'Yayınlar', val: displayUser?.streams ?? 0, action: null },
          ].map((s, i) => (
            <Col key={i} onClick={s.action || undefined} style={{ alignItems: 'center', cursor: s.action ? 'pointer' : 'default', padding: '0 10px' }}>
              <div className="syne" style={{ fontSize: 18, fontWeight: 800 }}>{s.val}</div>
              <div style={{ fontSize: 11, color: 'var(--muted)', marginTop: 2 }}>{s.label}</div>
            </Col>
          ))}
        </Row>

        {/* Action Buttons */}
        <Row style={{ gap: 10, marginBottom: 30 }}>
          {isOwnProfile ? (
            <>
              {isEditingProfile ? (
                <button 
                  onClick={handleSaveProfile} 
                  style={{ flex: 1, padding: 14, background: 'var(--green)', border: 'none', borderRadius: 16, color: '#fff', fontSize: 14, fontWeight: 700, fontFamily: 'Syne', cursor: 'pointer' }}
                >
                  Kaydet
                </button>
              ) : (
                <button 
                  onClick={() => setIsEditingProfile(true)} 
                  style={{ flex: 1, padding: 14, background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 16, color: 'var(--text)', fontSize: 14, fontWeight: 700, fontFamily: 'Syne', cursor: 'pointer' }}
                >
                  Profili Düzenle
                </button>
              )}
              <button 
                onClick={handleShare} 
                style={{ padding: '0 20px', background: isCopied ? 'var(--green)' : 'var(--surface)', border: isCopied ? '1px solid var(--green)' : '1px solid var(--border)', borderRadius: 16, color: isCopied ? '#000' : 'var(--text)', fontSize: 14, fontWeight: 600, cursor: 'pointer', transition: 'all 0.2s' }}
              >
                {isCopied ? 'Kopyalandı' : 'Paylaş'}
              </button>
            </>
          ) : (
            <>
              <button onClick={handleFollowUser} style={{ flex: 1, padding: 14, background: 'linear-gradient(135deg,var(--coral),var(--orange))', border: 'none', borderRadius: 16, color: '#fff', fontSize: 14, fontWeight: 700, fontFamily: 'Syne', cursor: 'pointer' }}>Takip Et</button>
              <button onClick={() => onMessageUser && onMessageUser(displayUser?._id)} style={{ padding: '0 20px', background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 16, color: 'var(--text)', fontSize: 14, fontWeight: 600, cursor: 'pointer' }}>Mesaj</button>
            </>
          )}
        </Row>

        {/* TABS */}
        <Row style={{ borderBottom: '1px solid var(--border)', marginBottom: 20 }}>
          {[
            { id: 'posts', label: 'Gönderiler' },
            { id: 'reels', label: 'Reels' },
            { id: 'tagged', label: 'Etiketlenenler' },
          ].map(t => (
            <div key={t.id} onClick={() => setTab(t.id)} style={{
              flex: 1, textAlign: 'center', padding: '12px 0', cursor: 'pointer',
              fontSize: 13, fontWeight: tab === t.id ? 700 : 500,
              color: tab === t.id ? 'var(--text)' : 'var(--muted)',
              borderBottom: tab === t.id ? '2px solid var(--text)' : '2px solid transparent',
              transition: 'all .2s'
            }}>
              {t.label}
            </div>
          ))}
        </Row>

        {/* Content Grid */}
        {tab === 'posts' && (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 2 }}>
            {/* Upload Tile - Only shown to the profile owner */}
            {isOwnProfile && (
              <div 
                onClick={() => postInputRef.current?.click()}
                style={{ aspectRatio: '1', background: 'var(--surface2)', cursor: 'pointer', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
                {isPosting ? (
                  <div style={{
                    width: 24, height: 24, borderRadius: '50%',
                    border: '3px solid rgba(255,255,255,0.3)', borderTopColor: '#fff',
                    animation: 'spin 1s linear infinite'
                  }} />
                ) : (
                  <>
                    <span style={{ fontSize: 24, color: 'var(--text)' }}>+</span>
                    <span style={{ fontSize: 12, color: 'var(--muted)', marginTop: 4 }}>Yeni Ekle</span>
                  </>
                )}
                <input type="file" ref={postInputRef} hidden accept="image/*" onChange={(e) => { e.stopPropagation(); handlePostUpload(e); }} onClick={(e) => e.stopPropagation()} />
              </div>
            )}
            
            {Array.isArray(posts) && posts.map(post => (
              <div 
                key={post._id} 
                onClick={() => setSelectedPost(post)}
                style={{ aspectRatio: '1', background: 'var(--surface2)', cursor: 'pointer', position: 'relative' }}>
                <img src={getImageUrl(post.imageUrl)} alt="Post" style={{ width: '100%', height: '100%', objectFit: 'cover' }} onError={(e) => e.currentTarget.style.display = 'none'} />
                {/* Overlay with like count on hover could be added here */}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Post Modal */}
      {selectedPost && (
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          background: 'rgba(0,0,0,0.9)', zIndex: 1000,
          display: 'flex', flexDirection: 'column',
          animation: 'fadeIn 0.2s ease-out'
        }}>
          {/* Header */}
          <Row style={{ padding: 16, alignItems: 'center', justifyContent: 'space-between', borderBottom: '1px solid var(--border)' }}>
            <div 
              style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer' }}
              onClick={() => {
                if(onViewProfile && selectedPost.user?._id) {
                  onViewProfile(selectedPost.user._id);
                  setSelectedPost(null);
                }
              }}
            >
              <img src={getImageUrl(selectedPost.user?.profileImage) || 'https://via.placeholder.com/40'} alt="User" style={{ width: 40, height: 40, borderRadius: '50%', objectFit: 'cover' }} />
              <div className="syne" style={{ fontWeight: 700, fontSize: 16 }}>{selectedPost.user?.username || 'Kullanıcı'}</div>
            </div>
            <Row style={{ alignItems: 'center', gap: 10 }}>
              {isOwnProfile && (
                <div onClick={() => handleDeletePost(selectedPost._id)} style={{ color: 'var(--coral)', cursor: 'pointer', padding: '0 8px', fontSize: 20 }} title="Sil">
                  🗑️
                </div>
              )}
              <div onClick={() => setSelectedPost(null)} style={{ fontSize: 24, cursor: 'pointer', padding: '0 8px' }}>✕</div>
            </Row>
          </Row>

          {/* Image */}
          <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#000', overflow: 'hidden' }}>
             <img src={getImageUrl(selectedPost.imageUrl)} alt="Post Large" style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }} />
          </div>

          {/* Actions & Comments */}
          <div style={{ 
            background: 'var(--bg)', 
            borderTopLeftRadius: 20, 
            borderTopRightRadius: 20, 
            padding: '16px 16px 80px 16px', // Extra bottom padding for navbar 
            maxHeight: '45vh', 
            display: 'flex', 
            flexDirection: 'column' 
          }}>
            <Row style={{ gap: 16, marginBottom: 12, alignItems: 'center' }}>
              <div onClick={() => handleLikePost(selectedPost._id)} style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6 }}>
                <span style={{ fontSize: 24, color: selectedPost.likes?.includes(user?.id || (user as any)?._id) ? 'var(--coral)' : 'var(--text)' }}>
                  {selectedPost.likes?.includes(user?.id || (user as any)?._id) ? '❤️' : '🤍'}
                </span>
                <span style={{ fontWeight: 600 }}>{selectedPost.likes?.length || 0}</span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <span style={{ fontSize: 22 }}>💬</span>
                <span style={{ fontWeight: 600 }}>{selectedPost.comments?.length || 0}</span>
              </div>
            </Row>

            {selectedPost.caption && (
              <div style={{ marginBottom: 16 }}>
                <span style={{ fontWeight: 700, marginRight: 8 }}>{selectedPost.user?.username}</span>
                <span>{selectedPost.caption}</span>
              </div>
            )}

            <div style={{ flex: 1, overflowY: 'auto', marginBottom: 16 }} className="hide-scrollbar">
               {Array.isArray(selectedPost.comments) && selectedPost.comments.map((c: any, i: number) => (
                 <Row key={i} style={{ marginBottom: 12, alignItems: 'flex-start', gap: 10 }}>
                    <img src={getImageUrl(c.user?.profileImage) || 'https://via.placeholder.com/30'} alt="User" style={{ width: 30, height: 30, borderRadius: '50%', objectFit: 'cover' }} />
                    <div>
                      <span style={{ fontWeight: 700, marginRight: 8, fontSize: 13 }}>{c.user?.username || 'Kullanıcı'}</span>
                      <span style={{ fontSize: 13 }}>{c.text}</span>
                    </div>
                 </Row>
               ))}
               {(!selectedPost.comments || selectedPost.comments.length === 0) && (
                 <div style={{ color: 'var(--muted)', fontSize: 13, textAlign: 'center', marginTop: 10 }}>Henüz yorum yok. İlk yorumu sen yap!</div>
               )}
            </div>

            <Row style={{ gap: 10 }}>
              <input 
                value={commentText}
                onChange={e => setCommentText(e.target.value)}
                placeholder="Yorum ekle..."
                style={{ flex: 1, background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 20, padding: '10px 16px', color: 'var(--text)' }}
                onKeyDown={e => { if (e.key === 'Enter') handleAddComment(selectedPost._id) }}
              />
              <button 
                className="syne"
                onClick={() => handleAddComment(selectedPost._id)}
                disabled={!commentText.trim()}
                style={{ 
                  background: 'var(--coral)', 
                  color: '#fff', 
                  border: 'none', 
                  borderRadius: 20, 
                  padding: '0 16px', 
                  fontWeight: 600, 
                  opacity: commentText.trim() ? 1 : 0.5,
                  cursor: 'pointer' 
                }}>
                Gönder
              </button>
            </Row>
          </div>
        </div>
      )}

      {/* Follow / Following List Modal */}
      {followModalType && (
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          background: 'rgba(0,0,0,0.85)', zIndex: 1050,
          display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'flex-end',
          animation: 'fadeIn 0.2s ease-out'
        }}>
          <div style={{
            background: 'var(--bg)', width: '100%', height: '85vh',
            borderTopLeftRadius: 24, borderTopRightRadius: 24,
            display: 'flex', flexDirection: 'column', overflow: 'hidden',
            boxShadow: '0 -10px 40px rgba(0,0,0,0.5)',
            animation: 'slideUp 0.3s cubic-bezier(0.16, 1, 0.3, 1)'
          }}>
            {/* Handle Bar */}
            <div style={{ width: '100%', display: 'flex', justifyContent: 'center', padding: '12px 0 8px' }} onClick={() => setFollowModalType(null)}>
              <div style={{ width: 40, height: 4, background: 'var(--surface2)', borderRadius: 2 }} />
            </div>
            
            {/* Modal Header */}
            <div style={{ padding: '0 20px 16px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderBottom: '1px solid var(--border)' }}>
              <h3 className="syne" style={{ margin: 0, fontSize: 18, fontWeight: 700 }}>
                {followModalType === 'followers' ? 'Takipçiler' : 'Takip Edilenler'}
              </h3>
              <div onClick={() => setFollowModalType(null)} style={{ cursor: 'pointer', padding: '5px', color: 'var(--muted)', fontSize: 20 }}>✕</div>
            </div>

            {/* List */}
            <div style={{ flex: 1, overflowY: 'auto', padding: '16px 20px', paddingBottom: 60 }}>
              {mockFollowList.map(item => (
                <div key={item._id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
                  <div 
                    style={{ display: 'flex', alignItems: 'center', gap: 12, cursor: 'pointer', flex: 1 }}
                    onClick={() => {
                      setFollowModalType(null);
                      if(onViewProfile) onViewProfile(item._id);
                    }}
                  >
                    <img src={item.profileImage} alt={item.username} style={{ width: 48, height: 48, borderRadius: 16, objectFit: 'cover' }} />
                    <div>
                      <div className="syne" style={{ fontSize: 15, fontWeight: 700, color: '#fff' }}>{item.username}</div>
                      <div style={{ fontSize: 12, color: 'var(--muted)' }}>@{item.username.toLowerCase()}</div>
                    </div>
                  </div>
                  <div>
                    <button style={{
                      background: item.isFollowing ? 'var(--surface2)' : 'linear-gradient(135deg, var(--coral), var(--orange))',
                      color: '#fff', border: 'none', borderRadius: 12, padding: '8px 16px',
                      fontFamily: 'Syne', fontWeight: 600, fontSize: 13, cursor: 'pointer'
                    }}>
                      {item.isFollowing ? 'Takipte' : 'Takip Et'}
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProfilePage;
